import type { Ref } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { OGSM_COPY } from '@/features/ogsm/ogsm-labels'
import { useOgsmShowInactive } from '@/hooks/use-ogsm-show-inactive'

export interface HiddenStrategiesChipProps {
  /** The backend's count of inactive children (`strategiesInactive`). */
  count: number
  ref?: Ref<HTMLButtonElement>
}

/**
 * "Show N hidden" / "Hide N inactive" under a band. Every chip and the title
 * row's button flip one shared state, and the chip keeps its element (and so
 * focus) when it flips.
 */
export function HiddenStrategiesChip({
  count,
  ref,
}: HiddenStrategiesChipProps) {
  const { showInactive, toggleShowInactive } = useOgsmShowInactive()
  if (count === 0) return null
  return (
    <Button
      ref={ref}
      variant="badge"
      size="badge"
      // Button sets every icon to 16px; the chip's glyph is 12px.
      className="shrink-0 [&_svg]:size-3"
      title={OGSM_COPY.inactiveScope}
      aria-label={
        showInactive
          ? OGSM_COPY.hideShownLabel(count)
          : OGSM_COPY.showHiddenLabel(count)
      }
      onClick={toggleShowInactive}
    >
      {showInactive ? <EyeOff /> : <Eye />}
      {showInactive ? OGSM_COPY.hideShown(count) : OGSM_COPY.showHidden(count)}
    </Button>
  )
}

/** The title row's board-wide toggle; the caller renders it only when the view has inactive strategies. */
export function ShowInactiveButton() {
  const { showInactive, toggleShowInactive } = useOgsmShowInactive()
  return (
    <Button
      variant="outline"
      title={OGSM_COPY.inactiveHint}
      onClick={toggleShowInactive}
    >
      {showInactive ? <EyeOff /> : <Eye />}
      {showInactive ? OGSM_COPY.hideInactive : OGSM_COPY.showInactive}
    </Button>
  )
}
